import { datadogRum } from '@datadog/browser-rum';
import type { QueryClient } from '@tanstack/react-query';
import has from 'lodash-es/has';
import type { LoaderFunctionArgs } from 'react-router';
import { redirect } from 'react-router';

import { setJwt } from '@jane/shared/auth';
import { ApiRequestError, getStoreQueryBody } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

import {
  ensureNumericId,
  trackKioskAuthenticated,
  trackingPath,
} from '../../../util';
import { getJwtQueryBody } from '../queries/jwt';
import { createSession } from '../queries/session';
import { getWhoamiQueryBody } from '../queries/whoami';

export const authenticateLoader =
  (queryClient: QueryClient) =>
  async ({ params, request }: LoaderFunctionArgs) => {
    const { storeId } = params;
    const numericStoreId = ensureNumericId(storeId);

    const url = new URL(request.url);
    const password = url.searchParams.get('password');
    const store = await queryClient.ensureQueryData(
      getStoreQueryBody<Store>(numericStoreId)
    );

    if (password && store.kiosk_id) {
      try {
        const { authHeader, body } = await createSession({
          id: store.kiosk_id,
          password: password || '',
          store_id: store.id,
          storeSlug: store.url_slug,
        });

        if (authHeader) {
          setJwt(authHeader);
        }
        await queryClient.ensureQueryData(getJwtQueryBody());
        await queryClient.ensureQueryData(
          getWhoamiQueryBody(authHeader, undefined)
        );
        if (has(body, 'kiosk')) {
          queryClient.setQueryData(
            ['whoami', authHeader, undefined],
            body.kiosk
          );
        }
        trackKioskAuthenticated({
          authDetails: {
            authType: 'url',
            backendError: null,
            path: trackingPath(url.pathname, url.search),
            success: true,
            userError: null,
          },
          store,
        });
      } catch (e) {
        let context: object = {};
        if (e instanceof ApiRequestError) {
          let userError: string[] | null = null;
          if (e.response.status >= 400 && e.response.status <= 499) {
            userError = ['Wrong password. Please try again.'];
          }
          trackKioskAuthenticated({
            authDetails: {
              authType: 'url',
              backendError: [e.message],
              path: trackingPath(url.pathname, url.search),
              success: false,
              userError,
            },
            store,
          });
          context = { url: e.response.url };
        }
        datadogRum.addError(e, context);
      }
    }

    return redirect(`/stores/${numericStoreId}`);
  };
